import React from 'react';
import { SimulationConfig } from '@/types';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';
import { Layout } from 'lucide-react';
import { CollapsibleSection } from './CollapsibleSection';

interface HUDSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  minPlan?: string;
  onUpgradeClick?: () => void;
}

export const HUDSettingsSection: React.FC<HUDSettingsSectionProps> = ({ 
    config, setConfig, isLocked, minPlan = "PRO", onUpgradeClick 
}) => {
  const updateConfig = (key: keyof SimulationConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  return (
    <CollapsibleSection 
        title="Interface Telemetry" 
        icon={Layout} 
        darkMode={config.darkMode} 
        isLocked={isLocked}
        lockRequiredPlan={minPlan as any}
        onUpgradeClick={onUpgradeClick}
        settingKey="hudSettings"
    >
        <div className="flex flex-col gap-6 mt-4 text-left">
            <div className="space-y-4">
                <ControlGroup settingKey="showHUD" label="Master HUD Interface" darkMode={config.darkMode}>
                    <Toggle active={config.showHUD} onClick={() => updateConfig('showHUD', !config.showHUD)} color="bg-cyan-600" />
                </ControlGroup>

                <ControlGroup settingKey="showDiagnosticsOverlay" label="Diagnostics Telemetry Overlay" tooltip="Live engine telemetry tracking real-time FPS, CPU loop steps vs GPU rendering command times, web worker SAB latency, and active draw-call loads." darkMode={config.darkMode}>
                    <Toggle active={config.showDiagnosticsOverlay || false} onClick={() => updateConfig('showDiagnosticsOverlay', !config.showDiagnosticsOverlay)} color="bg-emerald-600" />
                </ControlGroup>
                
                <ControlGroup settingKey="showControlIsland" label="Floating Control Hub" tooltip="Floating play/reset buttons at the bottom of the screen." darkMode={config.darkMode}>
                    <Toggle active={config.showControlIsland} onClick={() => updateConfig('showControlIsland', !config.showControlIsland)} />
                </ControlGroup>
            </div>
            
            {config.showHUD && (
                <div className="space-y-8 pt-6 border-t border-zinc-500/10 mt-2 animate-in fade-in slide-in-from-top-2 duration-500">
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                             <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                             <span className="text-[10px] font-black uppercase tracking-widest opacity-40">Positioning & Canvas</span>
                        </div>
                        <ControlGroup settingKey="hudPosition" label="Interface Anchor" darkMode={config.darkMode}>
                            <select 
                                value={config.hudPosition}
                                onChange={(e) => updateConfig('hudPosition', e.target.value)}
                                className={`w-full h-11 px-4 border rounded-xl text-[10px] font-black uppercase tracking-widest outline-none transition-all ${config.darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-zinc-200 text-zinc-900'}`}
                            >
                                <option value="TOP_LEFT">Top Left</option>
                                <option value="TOP_RIGHT">Top Right</option>
                                <option value="BOTTOM_LEFT">Bottom Left</option>
                                <option value="BOTTOM_RIGHT">Bottom Right</option>
                            </select>
                        </ControlGroup>

                        <div className="grid grid-cols-2 gap-4">
                            <SmartInput 
                                settingKey="hudOpacity"
                                label="Transparency" 
                                value={config.hudOpacity} 
                                min={0} max={1} step={0.1} 
                                onChange={(val) => updateConfig('hudOpacity', val)} 
                                darkMode={config.darkMode}
                            />
                            <SmartInput 
                                settingKey="hudScale"
                                label="Unit Scale" 
                                value={config.hudScale} 
                                min={0.5} max={2} step={0.1} 
                                onChange={(val) => updateConfig('hudScale', val)} 
                                darkMode={config.darkMode}
                            />
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                             <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                             <span className="text-[10px] font-black uppercase tracking-widest opacity-40">Telemetry Channels</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                            {[
                                { key: 'hudShowElapsedTime', label: 'Elapsed Time' },
                                { key: 'hudShowSpeed', label: 'Velocity Vector' },
                                { key: 'hudShowBallCount', label: 'Sphere Population' },
                                { key: 'hudShowBounceCount', label: 'Collision Count' },
                                { key: 'hudShowRingsLeft', label: 'Rings Remaining' },
                                { key: 'hudShowTimeRemaining', label: 'Time Registry' },
                                { key: 'hudShowFPS', label: 'Performance FPS' },
                                { key: 'hudShowCurrentRound', label: 'Active Round' },
                                { key: 'hudShowContainerRadius', label: 'Arena Boundary' },
                                { key: 'hudShowBallRadius', label: 'Entity Scale' },
                                { key: 'hudShowGravity', label: 'Force Gravity' },
                                { key: 'hudShowRestitution', label: 'Elastic Bounce' },
                                { key: 'hudShowBallMass', label: 'Entity Mass' },
                                { key: 'hudShowTimeScale', label: 'Chrono Scale' },
                                { key: 'hudShowInitialSpeed', label: 'Base Velocity' },
                                { key: 'hudShowSpeedIncreaseRate', label: 'Acceleration' },
                                { key: 'hudShowAirResistance', label: 'Atmospheric Drag' },
                                { key: 'hudShowPhysicsSubsteps', label: 'Integrator Steps' },
                                { key: 'hudShowBallGrowthRate', label: 'Expansion Rate' },
                                { key: 'hudShowContainerShrinkRate', label: 'Collapse Rate' },
                            ].map((item: any) => (
                                <ControlGroup 
                                    key={item.key} 
                                    settingKey={item.key}
                                    label={item.label} 
                                    darkMode={config.darkMode}
                                >
                                    <Toggle 
                                        active={config[item.key as keyof SimulationConfig] as boolean} 
                                        onClick={() => updateConfig(item.key as keyof SimulationConfig, !config[item.key as keyof SimulationConfig])} 
                                    />
                                </ControlGroup>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    </CollapsibleSection>
  );
};
